import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Route, MapPin, ExternalLink, AlertCircle, AlertTriangle, Clock, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const TourenplanungPage = () => {
  const { API } = useContext(AuthContext);
  const [tourItems, setTourItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/tourenplanung`);
      setTourItems(response.data || []);
    } catch (error) {
      // Nur bei echten Serverfehlern einen Toast zeigen
      if (error.response?.status >= 500) {
        toast.error('Serverfehler beim Laden der Tourenplanung');
      } else if (error.response?.status === 403) {
        toast.error('Keine Berechtigung für Tourenplanung');
      }
      // Bei anderen Fehlern: leere Liste anzeigen ohne Toast
      setTourItems([]);
      console.error('Tourenplanung laden:', error.response?.status || error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsEmptied = async (automatId) => {
    try {
      await axios.post(`${API}/automaten/${automatId}/leeren`);
      toast.success('Automat als geleert markiert!');
      fetchData(); // Reload data
    } catch (error) {
      toast.error('Fehler beim Markieren des Automaten');
    }
  };

  const openGoogleMaps = (standort) => {
    if (standort.latitude && standort.longitude) {
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${standort.latitude},${standort.longitude}`,
        '_blank'
      );
    } else {
      const address = `${standort.street}, ${standort.zip} ${standort.city}`;
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`,
        '_blank'
      );
    }
  };

  const openMultipleLocations = () => {
    const locations = tourItems
      .filter(item => item.standort && item.standort.latitude && item.standort.longitude)
      .slice(0, 10)
      .map(item => `${item.standort.latitude},${item.standort.longitude}`);
    
    if (locations.length > 0) {
      const origin = locations[0];
      const destination = locations[locations.length - 1];
      const waypoints = locations.slice(1, -1).join('|');
      
      const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}${waypoints ? `&waypoints=${waypoints}` : ''}`;
      window.open(url, '_blank');
    } else {
      toast.error('Keine Standorte mit Koordinaten verfügbar');
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Laden...</div>;
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="tourenplanung-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            Tourenplanung
          </h1>
          <p className="text-slate-600">Einsatzrelevante Automaten - Leerung & Reparaturen</p>
          <div className="flex items-center space-x-4 mt-2 text-sm text-slate-500">
            <span>📊 {tourItems.length} Aufgaben</span>
            <span>⏰ Überfällige Leerungen: {tourItems.filter(i => i.days_overdue_emptying > 0).length}</span>
            <span>🔴 Defekte im Gehäuse: {tourItems.filter(i => i.is_defect).length}</span>
          </div>
        </div>
        {tourItems.length > 0 && (
          <Button
            onClick={openMultipleLocations}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700"
            data-testid="open-route-button"
          >
            <Route size={20} />
            <span>Route in Google Maps öffnen</span>
          </Button>
        )}
      </div>

      {tourItems.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <CheckCircle className="mx-auto text-green-500 mb-4" size={64} />
            <p className="text-slate-700 text-xl font-semibold">Keine offenen Aufgaben!</p>
            <p className="text-slate-500 text-sm mt-2">Alle Automaten sind aktuell, keine überfälligen Leerungen oder defekte Automaten im Gehäuse.</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {tourItems.map((item, index) => {
            // Determine card styling based on urgency
            let cardBorder = 'border-slate-200';
            let cardBg = 'bg-white';
            
            // Determine styling based on location defect
            if (item.is_location_defect && !item.automat) {
              // Pure location defect - orange styling
              cardBorder = 'border-orange-500 border-2';
              cardBg = 'bg-orange-50';
            } else if (item.is_location_defect && item.is_defect) {
              // Both location and machine defect - red/orange mix
              cardBorder = 'border-red-500 border-2';
              cardBg = 'bg-red-50';
            } else if (item.is_defect && item.days_overdue_emptying > 0) {
              // Both conditions: red with pulse
              cardBorder = 'border-red-500 border-2';
              cardBg = 'bg-red-50 animate-pulse-slow';
            } else if (item.days_overdue_emptying >= 7) {
              // Very overdue
              cardBorder = 'border-orange-500 border-2';
              cardBg = 'bg-orange-50';
            } else if (item.days_overdue_emptying > 0) {
              // Slightly overdue
              cardBorder = 'border-yellow-500';
              cardBg = 'bg-yellow-50';
            } else if (item.is_defect) {
              // Just defect
              cardBorder = 'border-red-400';
              cardBg = 'bg-red-50';
            }
            
            return (
              <Card
                key={`${item.standort.id}-${item.automat ? item.automat.id : 'location-defect'}`}
                className={`p-6 ${cardBorder} ${cardBg}`}
                data-testid={`tour-item-${index}`}
              >
                <div className="flex flex-col lg:flex-row justify-between gap-4">
                  {/* Left side: Location and Machine Info */}
                  <div className="flex-1">
                    <div className="flex items-start space-x-4">
                      {/* Priority Badge */}
                      <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${
                        item.priority >= 999 ? 'bg-red-600' :
                        item.priority >= 7 ? 'bg-orange-500' :
                        item.priority > 0 ? 'bg-yellow-500' :
                        'bg-blue-500'
                      }`}>
                        {index + 1}
                      </div>
                      
                      <div className="flex-1">
                        {/* Standort Info */}
                        <div className="mb-3">
                          <Link 
                            to={`/standorte/${item.standort.id}`}
                            className="text-xl font-bold text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {item.standort.name}
                          </Link>
                          <p className="text-sm text-slate-600">
                            <MapPin className="inline mr-1" size={14} />
                            {item.standort.street}, {item.standort.zip} {item.standort.city}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">Code: {item.standort.code}</p>
                        </div>
                        
                        {/* Automat Info - nur wenn Automat vorhanden */}
                        {item.automat && (
                          <div className="bg-white/70 rounded-lg p-3 border border-slate-200">
                            <div className="flex items-center justify-between mb-2">
                              <Link 
                                to={`/automaten/${item.automat.id}`}
                                className="font-bold text-slate-800 hover:text-blue-600 hover:underline"
                              >
                                🤖 {item.automat.code}
                              </Link>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                item.automat.status === 'defekt' ? 'bg-red-200 text-red-800' :
                                item.automat.status === 'AKTIV' ? 'bg-green-200 text-green-800' :
                                item.automat.status === 'Lager VOLL' ? 'bg-purple-200 text-purple-800' :
                                'bg-slate-200 text-slate-800'
                              }`}>
                                {item.automat.status}
                              </span>
                            </div>
                            <p className="text-xs text-slate-600">
                              {item.automat.machine_type} • Slot {item.automat.current_slot_number}
                            </p>
                          </div>
                        )}
                        
                        {/* Location-only defect indicator */}
                        {!item.automat && item.is_location_defect && (
                          <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                            <p className="text-sm font-semibold text-orange-800">
                              📍 Standort-Problem (kein spezifischer Automat betroffen)
                            </p>
                          </div>
                        )}
                        
                        {/* Hints/Warnings */}
                        <div className="mt-3 space-y-2">
                          {item.hints.map((hint, hintIndex) => {
                            // Determine hint type and styling
                            const isLocationDefect = hint.includes('📍 Standort defekt');
                            const isMachineDefect = hint.includes('🔴 Automat defekt');
                            const isOverdue = hint.includes('überfällig');
                            
                            let hintStyle = 'bg-blue-100 border-blue-300';
                            let iconColor = 'text-blue-600';
                            let icon = <Clock className="flex-shrink-0 mt-0.5" size={16} />;
                            
                            if (isLocationDefect) {
                              hintStyle = 'bg-orange-100 border-orange-300';
                              iconColor = 'text-orange-600';
                              icon = <MapPin className="flex-shrink-0 mt-0.5" size={16} />;
                            } else if (isMachineDefect) {
                              hintStyle = 'bg-red-100 border-red-300';
                              iconColor = 'text-red-600';
                              icon = <AlertTriangle className="flex-shrink-0 mt-0.5" size={16} />;
                            } else if (isOverdue) {
                              hintStyle = 'bg-yellow-100 border-yellow-300';
                              iconColor = 'text-yellow-600';
                              icon = <Clock className="flex-shrink-0 mt-0.5" size={16} />;
                            }
                            
                            return (
                              <div
                                key={hintIndex}
                                className={`flex items-start space-x-2 p-2 rounded-lg ${hintStyle} border`}
                              >
                                <span className={iconColor}>{icon}</span>
                                <span className="text-sm font-medium text-slate-800">{hint}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Right side: Actions */}
                  <div className="flex flex-col space-y-2 lg:w-48">
                    <Button
                      onClick={() => openGoogleMaps(item.standort)}
                      variant="outline"
                      size="sm"
                      className="w-full"
                    >
                      <MapPin size={16} className="mr-2" />
                      Navigation
                    </Button>
                    
                    {item.days_overdue_emptying > 0 && item.automat && (
                      <Button
                        onClick={() => handleMarkAsEmptied(item.automat.id)}
                        className="w-full bg-green-600 hover:bg-green-700"
                        size="sm"
                      >
                        <CheckCircle size={16} className="mr-2" />
                        Als geleert markieren
                      </Button>
                    )}
                    
                    {item.automat && (
                      <Link to={`/automaten/${item.automat.id}`}>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                        >
                          <ExternalLink size={16} className="mr-2" />
                          Automat-Details
                        </Button>
                      </Link>
                    )}
                    
                    {item.is_location_defect && (
                      <Link to={`/standorte/${item.standort.id}`}>
                        <Button
                          className="w-full bg-orange-600 hover:bg-orange-700"
                          size="sm"
                        >
                          <ExternalLink size={16} className="mr-2" />
                          Standort bearbeiten
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TourenplanungPage;
